import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { Empty } from "antd";
import { toast } from "react-toastify";
import IconButton from "components/common/IconButton";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import useCancellationPolicyRules from "hooks/cancellation-policy/useCancellationPolicyRules";
import CancellationPolicyFormModal, {
  CANCELLER_OPTIONS,
  STAGE_OPTIONS,
} from "./CancellationPolicyFormModal";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
};

const badgeBg = (rule) => (rule?.active ? "success" : "secondary");

const CancellationPolicyActivePage = () => {
  const {
    loading,
    getActiveRules,
    getActiveRuleByStage,
    createRule,
    updateRule,
    deleteRule,
  } = useCancellationPolicyRules();

  const [rules, setRules] = useState([]);
  const [selectedStage, setSelectedStage] = useState("");
  const [selectedCanceler, setSelectedCanceler] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const [showDelete, setShowDelete] = useState(false);
  const [deletingRuleId, setDeletingRuleId] = useState(null);
  const [mutating, setMutating] = useState(false);

  const loadActiveRules = useCallback(async () => {
    try {
      const data = await getActiveRules();
      setRules(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.message || "Failed to load active cancellation rules");
    }
  }, [getActiveRules]);

  useEffect(() => {
    loadActiveRules();
  }, [loadActiveRules]);

  const handleFilter = async () => {
    if (!selectedStage || !selectedCanceler) {
      await loadActiveRules();
      return;
    }

    try {
      const rule = await getActiveRuleByStage(selectedStage, selectedCanceler);
      setRules(rule ? [rule] : []);
    } catch (err) {
      setRules([]);
      toast.error(err?.message || "Failed to filter active rules");
    }
  };

  const clearFilters = async () => {
    setSelectedStage("");
    setSelectedCanceler("");
    await loadActiveRules();
  };

  const onCreate = async (payload) => {
    setMutating(true);
    try {
      await createRule(payload);
      toast.success("Cancellation rule created");
      setShowForm(false);
      setEditingRule(null);
      await loadActiveRules();
    } catch (err) {
      toast.error(err?.message || "Failed to create rule");
    } finally {
      setMutating(false);
    }
  };

  const onUpdate = async (payload) => {
    if (!editingRule?.id) return;
    setMutating(true);
    try {
      await updateRule(editingRule.id, payload);
      toast.success("Cancellation rule updated");
      setShowForm(false);
      setEditingRule(null);
      await loadActiveRules();
    } catch (err) {
      toast.error(err?.message || "Failed to update rule");
    } finally {
      setMutating(false);
    }
  };

  const onDelete = async () => {
    if (!deletingRuleId) return;
    setMutating(true);
    try {
      await deleteRule(deletingRuleId);
      toast.success("Cancellation rule deleted");
      setShowDelete(false);
      setDeletingRuleId(null);
      await loadActiveRules();
    } catch (err) {
      toast.error(err?.message || "Failed to delete rule");
      throw err;
    } finally {
      setMutating(false);
    }
  };

  const cards = useMemo(
    () =>
      rules.map((rule) => (
        <Col lg={6} key={rule.id}>
          <Card className="h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
                <div>
                  <h6 className="mb-1">{rule.stage || "-"}</h6>
                  <small className="text-muted">{rule.canceler || "-"}</small>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Badge bg={badgeBg(rule)}>{rule.active ? "ACTIVE" : "INACTIVE"}</Badge>
                  <Dropdown>
                    <Dropdown.Toggle as="button" className="bg-transparent border-0 text-secondary">
                      ...
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item
                        onClick={() => {
                          setEditingRule(rule);
                          setShowForm(true);
                        }}
                      >
                        Edit
                      </Dropdown.Item>
                      <Dropdown.Item
                        className="text-danger"
                        onClick={() => {
                          setDeletingRuleId(rule.id);
                          setShowDelete(true);
                        }}
                      >
                        Delete
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>

              <div className="small text-muted">Description</div>
              <div className="mb-3">{rule.description || "-"}</div>

              <Row className="g-2 text-sm">
                <Col md={6}>
                  <div>
                    <strong>Charge %:</strong> {rule.chargePercent ?? 0}
                  </div>
                  <div>
                    <strong>Charge Fixed:</strong> {rule.chargeFixedAmount ?? 0}
                  </div>
                  <div>
                    <strong>Charge Base:</strong> {rule.chargeBase || "-"}
                  </div>
                </Col>
                <Col md={6}>
                  <div>
                    <strong>Effective From:</strong> {formatDateTime(rule.effectiveFrom)}
                  </div>
                  <div>
                    <strong>Effective To:</strong> {formatDateTime(rule.effectiveTo)}
                  </div>
                  <div>
                    <strong>Reason Required:</strong> {rule.requireReason ? "Yes" : "No"}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      )),
    [rules],
  );

  return (
    <>
      <WidgetSectionTitle
        className="mt-4 mb-4"
        title="Cancellation Policy Active Rules"
        icon="ban"
        subtitle="View and manage currently active cancellation rules."
      />

      <Card className="mb-4">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Stage</Form.Label>
                <Form.Select value={selectedStage} onChange={(e) => setSelectedStage(e.target.value)}>
                  <option value="">All stages</option>
                  {STAGE_OPTIONS.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Canceler</Form.Label>
                <Form.Select value={selectedCanceler} onChange={(e) => setSelectedCanceler(e.target.value)}>
                  <option value="">All cancelers</option>
                  {CANCELLER_OPTIONS.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4} className="d-flex gap-2">
              <Button variant="primary" onClick={handleFilter}>
                Apply filter
              </Button>
              <Button variant="outline-secondary" onClick={clearFilters}>
                Reset
              </Button>
              <IconButton
                variant="falcon-default"
                size="sm"
                icon="plus"
                onClick={() => {
                  setEditingRule(null);
                  setShowForm(true);
                }}
              >
                <span className="ms-1">Add Rule</span>
              </IconButton>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 220 }}>
          <Spinner animation="border" variant="success" />
        </div>
      ) : rules.length === 0 ? (
        <Alert variant="light" className="border">
          <Empty description="No active cancellation rules found" />
        </Alert>
      ) : (
        <Row className="g-3">{cards}</Row>
      )}

      <CancellationPolicyFormModal
        show={showForm}
        onClose={() => {
          if (mutating) return;
          setShowForm(false);
          setEditingRule(null);
        }}
        onSubmit={editingRule ? onUpdate : onCreate}
        loading={mutating}
        initialRule={editingRule}
        title={editingRule ? "Edit Active Cancellation Rule" : "Create Cancellation Rule"}
      />

      <ConfirmDeleteModal
        show={showDelete}
        setShow={setShowDelete}
        onConfirm={onDelete}
        itemName="cancellation policy rule"
        loading={mutating}
      />
    </>
  );
};

export default CancellationPolicyActivePage;
